import React from 'react';
import { Link } from 'react-router-dom';
import './TermsOfUse.css';

const TermsOfUse = () => {
  return (
    <div className="terms-of-use">
      <header className="header">
        <div className="container">
          <div className="header-left">
            <Link to="/" className="logo">
              <img src="/assets/ic_kidstrail.png" alt="KidsTrail" className="logo-icon" />
              KidsTrail
            </Link>
          </div>
          <div className="header-center">
            <div className="header-download-buttons">
              <a
                href="https://apps.apple.com/app/id6749541872"
                className="header-download-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/assets/ic_applestore.png" alt="Download on App Store" className="header-store-icon" />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.kidstrail.app"
                className="header-download-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/assets/ic_playstore.png" alt="Get it on Google Play" className="header-store-icon" />
              </a>
            </div>
            <div className="header-social-buttons">
              <a
                href="https://www.youtube.com/@KidsTrailCare"
                className="header-social-btn"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
              >
                <img src="/assets/ic_youtube.png" alt="YouTube" className="header-social-icon" />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61585649347570"
                className="header-social-btn"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <img src="/assets/ic_facebook.png" alt="Facebook" className="header-social-icon" />
              </a>
              <a
                href="https://www.instagram.com/kidstrail.care/"
                className="header-social-btn"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <img src="/assets/ic_instagram.png" alt="Instagram" className="header-social-icon" />
              </a>
            </div>
          </div>
        </div>
      </header>

      <main className="main">
        <div className="container">
          <div className="policy-content">
            <h1>Terms of Use</h1>
            <p className="last-updated">Last updated: {new Date().toLocaleDateString()}</p>

            <section className="policy-section">
              <h2>Acceptance of Terms</h2>
              <p>
                These Terms of Use are an agreement between you and KidsTrail. They cover the
                KidsTrail mobile application, the kidstrail.net website, and everything we offer
                through them. By downloading, installing, or using KidsTrail, you agree to these
                terms. If you do not agree, please stop using the app and remove it from your
                device.
              </p>
              <p>
                Our <Link to="/privacy-policy">Privacy Policy</Link> explains how we handle
                information and forms part of this agreement.
              </p>
            </section>

            <section className="policy-section">
              <h2>Eligibility and Parental Consent</h2>
              <p>
                KidsTrail is built for young children, and the app is used under adult supervision.
                The account holder is always the parent or legal guardian, who must be at least 18
                years old and legally able to enter into this agreement.
              </p>
              <div className="highlight-box">
                <h3>👨‍👩‍👧 For Parents and Guardians</h3>
                <ul>
                  <li>
                    <strong>You accept these terms on behalf of your child.</strong> By letting your
                    child use KidsTrail, you consent to their use of the app under these terms.
                  </li>
                  <li>
                    <strong>Children may not create an account.</strong> Sign-in, purchases, and
                    settings are for the adult account holder only.
                  </li>
                  <li>
                    <strong>Supervision matters.</strong> Please check the content your child sees
                    and use the parental control PIN to restrict areas you would rather keep closed.
                  </li>
                  <li>
                    <strong>COPPA.</strong> We do not knowingly collect personal information from
                    children under 13. Where any such information would be involved, we rely on your
                    verifiable consent as the parent. You can withdraw that consent at any time by
                    writing to care@kidstrail.net.
                  </li>
                </ul>
              </div>
            </section>

            <section className="policy-section">
              <h2>Licence to Use the App</h2>
              <p>
                We grant you a personal, non-commercial, non-transferable, non-exclusive, revocable
                licence to install and use KidsTrail on devices you own or control, for your family's
                own learning and play.
              </p>
              <p>You may not:</p>
              <ul>
                <li>Sell, rent, sublicense, or redistribute the app or any of its content</li>
                <li>Use KidsTrail content in a classroom, nursery, or business setting without a written agreement from us</li>
                <li>Copy, modify, translate, reverse engineer, or create derivative works from the app, except where the law allows it</li>
                <li>Remove or obscure any copyright, trademark, or attribution notice</li>
              </ul>
              <p>
                Printable worksheets and books are meant for home printing for the children in your
                own household. Redistributing or reselling printed material is not permitted.
              </p>
            </section>

            <section className="policy-section">
              <h2>Accounts and Child Profiles</h2>
              <ul>
                <li>
                  Parents may sign in with Apple or Google. You are responsible for keeping your
                  sign-in credentials and your parental control PIN private.
                </li>
                <li>
                  You may create child profiles inside your account. Profiles are for organising
                  progress and preferences, and they are managed by you.
                </li>
                <li>
                  Please give profiles a first name or a nickname. Do not enter contact details,
                  addresses, school names, or other identifying information about a child.
                </li>
                <li>
                  Activity that happens under your account is your responsibility. Tell us at
                  care@kidstrail.net if you believe someone else has accessed it.
                </li>
                <li>
                  You may delete a profile, your content, or the whole account from within the app,
                  or request deletion through our{' '}
                  <Link to="/request-account-delete">account deletion page</Link>.
                </li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>Subscriptions, Free Trial, Auto-Renewal and Cancellation</h2>
              <p>
                KidsTrail is free to download. Some features are part of an optional paid
                subscription, which also removes advertising. Prices, plan lengths, and trial
                periods are shown in the app before you confirm a purchase.
              </p>
              <ul>
                <li>
                  If a free trial is offered, it converts into a paid subscription when it ends
                  unless you cancel first.
                </li>
                <li>
                  Subscriptions renew automatically for the same period until cancelled. Your store
                  account is charged within 24 hours before the current period ends.
                </li>
                <li>
                  Cancelling stops future renewals. Access continues until the end of the period you
                  have already paid for.
                </li>
                <li>
                  Payment is taken by Apple or Google, and we never see or store your card details.
                </li>
              </ul>
              <div className="highlight-box">
                <h3>💳 How to Cancel</h3>
                <p>
                  <strong>Billing runs through the Apple App Store or Google Play, so cancellation
                  happens there.</strong> Emailing us does not cancel a subscription.
                </p>
                <ul>
                  <li>
                    <strong>iOS:</strong> Settings → your Apple Account → Subscriptions → KidsTrail →
                    Cancel Subscription.
                  </li>
                  <li>
                    <strong>Android:</strong> Google Play → your profile → Payments and subscriptions
                    → Subscriptions → KidsTrail → Cancel.
                  </li>
                </ul>
                <p>
                  Refunds are handled by the store under Apple's and Google's own policies. We can
                  sometimes help by contacting the store on your behalf, and we will if you ask, but
                  the final decision rests with them.
                </p>
              </div>
              <p>
                Deleting the app does not cancel a subscription. Please cancel in your store account
                before uninstalling.
              </p>
            </section>

            <section className="policy-section">
              <h2>Advertising</h2>
              <ul>
                <li>
                  Users on the free tier see small advertisements served by Google AdMob. Subscribers
                  see none.
                </li>
                <li>
                  Every ad request is tagged as child-directed and under the age of consent, ads are
                  non-personalised, and the maximum ad content rating is set to G for general
                  audiences.
                </li>
                <li>
                  Advertisements come from a third-party network. We do not control what any single
                  advertiser produces, and we do not endorse advertised products or services.
                </li>
                <li>
                  If you see an ad that seems inappropriate for children, please send us a screenshot
                  at care@kidstrail.net so we can report and block it.
                </li>
              </ul>
              <p>
                More detail about ad-related data is in our{' '}
                <Link to="/privacy-policy">Privacy Policy</Link>.
              </p>
            </section>

            <section className="policy-section">
              <h2>User-Generated Content</h2>
              <p>
                KidsTrail lets you and your child make things: drawings, custom animated books, voice
                recordings, and profile details. We call all of this "your content".
              </p>
              <ul>
                <li>
                  <strong>You keep ownership.</strong> Your content belongs to you and your family.
                  We do not claim ownership of it.
                </li>
                <li>
                  <strong>The licence you give us is narrow.</strong> You grant us a limited,
                  worldwide, royalty-free licence to store, copy, transmit, and display your content
                  only so we can run the service for you, for example syncing between your devices,
                  cloud backup, and rendering a custom book. This licence ends when you delete the
                  content, apart from copies held in routine backups for a short period.
                </li>
                <li>
                  <strong>We do not publish your content.</strong> Nothing you create is shared with
                  other users or used in our marketing without your separate written permission.
                </li>
                <li>
                  <strong>You are responsible for what you upload.</strong> Only add content you have
                  the right to use, including photographs and recordings of other people. Ask before
                  you include someone else's likeness or voice.
                </li>
                <li>
                  <strong>We can remove content.</strong> If content breaks these terms, is illegal,
                  or is unsafe for children, we may remove it or suspend access, and we will tell you
                  why where we are able to.
                </li>
                <li>
                  <strong>Keep your own copies.</strong> Export or print anything you would be sad to
                  lose. We are not a backup service of record.
                </li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>Acceptable Use and Prohibited Conduct</h2>
              <p>When using KidsTrail, you agree not to:</p>
              <ul>
                <li>Break any applicable law or infringe anyone's rights</li>
                <li>Upload content that is obscene, hateful, violent, or otherwise unsuitable for a children's app</li>
                <li>Attempt to access another user's account, content, or data</li>
                <li>Probe, scan, or disrupt our systems, or try to bypass security or the parental control PIN</li>
                <li>Use bots, scrapers, or automated tools to extract app content</li>
                <li>Tamper with subscription checks, unlock paid features without paying, or use fraudulent payment details</li>
                <li>Impersonate anyone or misrepresent your relationship to a child using the app</li>
                <li>Interfere with advertising delivery or measurement</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>Third-Party Content and Links</h2>
              <p>
                KidsTrail includes curated video playlists that play through YouTube's embedded
                player. That video content is hosted and delivered by YouTube, which is operated by
                Google, and your use of it is subject to{' '}
                <a
                  href="https://www.youtube.com/t/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  YouTube's Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google's Privacy Policy
                </a>
                .
              </p>
              <ul>
                <li>
                  We select the channels and videos we consider suitable, and we review them, though
                  we do not own or control them. A third party can change or remove a video at any
                  time.
                </li>
                <li>
                  The app may also link to external websites and to the App Store and Google Play. We
                  are not responsible for the content or practices of those services.
                </li>
                <li>
                  If a video or link no longer looks right for children, tell us at care@kidstrail.net
                  and we will review it quickly.
                </li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>Intellectual Property</h2>
              <p>
                The KidsTrail name, logo, app design, illustrations, animations, audio, worksheets,
                books, quizzes, games, and software are owned by us or licensed to us, and are
                protected by copyright, trademark, and other laws. Apart from the licence described
                above, no rights are transferred to you.
              </p>
              <p>
                If you believe something in KidsTrail infringes your copyright, write to
                care@kidstrail.net with a description of the work, where it appears in the app, and
                your contact details. We will investigate and act where a claim is valid.
              </p>
            </section>

            <section className="policy-section">
              <h2>Availability, Updates and Changes to the Service</h2>
              <ul>
                <li>
                  We may add, change, or retire features, content, and language options as the app
                  grows.
                </li>
                <li>
                  Updates may be required for the app to keep working. Some features need a recent
                  operating system version or an internet connection.
                </li>
                <li>
                  Service may be interrupted by maintenance, network problems, or issues at a
                  third-party provider.
                </li>
                <li>
                  If we ever discontinue a paid feature during a period you have paid for, we will
                  give notice in the app and work with the store on a fair remedy.
                </li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>Disclaimer of Warranties</h2>
              <p>
                KidsTrail is provided "as is" and "as available". To the fullest extent permitted by
                law, we disclaim all warranties, whether express or implied, including
                merchantability, fitness for a particular purpose, and non-infringement.
              </p>
              <p>
                KidsTrail is an educational and entertainment product. We do not promise any specific
                learning outcome, and the app is no substitute for professional educational,
                developmental, or medical advice. Some jurisdictions do not allow certain warranty
                exclusions, so parts of this section may not apply to you.
              </p>
            </section>

            <section className="policy-section">
              <h2>Limitation of Liability</h2>
              <p>
                To the fullest extent permitted by law, KidsTrail and its team will not be liable for
                indirect, incidental, special, consequential, or punitive damages, or for lost data,
                lost content, or lost profits, arising from your use of the app.
              </p>
              <p>
                Our total liability for any claim relating to the app is limited to the amount you
                paid us for KidsTrail in the twelve months before the claim arose, or 50 USD,
                whichever is greater. Nothing here limits liability that cannot be limited by law,
                including for death or personal injury caused by negligence, or for fraud. Where
                local consumer law gives you rights that cannot be waived, those rights still apply.
              </p>
            </section>

            <section className="policy-section">
              <h2>Indemnity</h2>
              <p>
                You agree to indemnify and hold harmless KidsTrail and its team from claims,
                liabilities, damages, and reasonable legal costs arising from your misuse of the app,
                your breach of these terms, content you upload, or your violation of the rights of a
                third party.
              </p>
            </section>

            <section className="policy-section">
              <h2>Termination</h2>
              <ul>
                <li>
                  You may stop using KidsTrail at any time by deleting your account in the app and
                  uninstalling it. Remember to cancel any active subscription in your store account
                  first.
                </li>
                <li>
                  We may suspend or terminate access if you breach these terms, if we are required to
                  by law, or if continued access poses a risk to other users or to children.
                </li>
                <li>
                  On termination, your licence ends immediately. Data handling after termination
                  follows the retention rules in our{' '}
                  <Link to="/privacy-policy">Privacy Policy</Link>.
                </li>
                <li>
                  Sections that by their nature should survive termination, such as intellectual
                  property, disclaimers, liability limits, and indemnity, continue to apply.
                </li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>Governing Law</h2>
              <p>
                These terms are governed by the laws of England and Wales, without regard to
                conflict-of-law rules, and disputes will be handled by the courts of England and
                Wales. If you are a consumer resident elsewhere, you keep the protection of the
                mandatory consumer laws of your country of residence and may bring proceedings there.
              </p>
              <p>
                Purchases made through the App Store or Google Play are also subject to the terms of
                the store you bought from.
              </p>
            </section>

            <section className="policy-section">
              <h2>Changes to These Terms</h2>
              <p>
                We may update these terms as the app changes or as the law requires. The revised
                version is posted on this page with a new "last updated" date, and material changes
                are announced in the app. Continuing to use KidsTrail after a change means you accept
                the updated terms.
              </p>
            </section>

            <section className="policy-section">
              <h2>Contact</h2>
              <p>
                Questions about these terms, or about anything you have seen in the app, are welcome:
              </p>
              <div className="contact-info">
                <p>Email: care@kidstrail.net</p>
                <p>Content Issues: care@kidstrail.net</p>
                <p>Subject: Terms of Use Inquiry</p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <footer className="footer">
        <div className="container">
          <p>&copy; 2026 KidsTrail. All rights reserved.</p>
          <div className="footer-links">
            <Link to="/" className="footer-link">Home</Link>
            <Link to="/contact-us" className="footer-link">Contact Us</Link>
            <Link to="/terms-of-use" className="footer-link">Terms of Use</Link>
            <Link to="/privacy-policy" className="footer-link">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfUse;
